import debug from 'debug';

// GQL
import { client } from '../client';
import { GET_COMMUNICATIONS_BY_IDS } from '../queries/communication';

const log = debug('rois:gql:selectors:warmup');

// Límite del contrato backend: `communicationsByIds` acepta máx 200 ids por
// llamada. Los detalles de OT rondan las decenas de hijas, el troceo es
// defensivo.
const BATCH_SIZE = 200;

/**
 * Warm-up de la caché normalizada de Apollo para las Communications de las
 * hijas del detalle de OT/campaña (y de los informes combinados).
 *
 * Resuelve las Communications de todas las hijas en UNA sola query
 * `communicationsByIds` (fragment NewCommunication, el MISMO de
 * GET_COMMUNICATION_BY_ID — el batch backend proyecta un superset del type y
 * Communication NO tiene @ResolveFields: paridad completa sin caveats,
 * verificado en la integración de rois-back). Las entidades
 * `Communication:<id>` quedan normalizadas en caché, de modo que los
 * `client.query(GET_COMMUNICATION_BY_ID, cache-first)` per-row que vienen
 * después se sirven de caché vía el redirect
 * `Query.communication → toReference` de policies.ts, en lugar de disparar
 * C requests fila a fila (el N+1 GetCommunicationById del detalle).
 *
 * REGLA DE ORO — es una OPTIMIZACIÓN, jamás un gate: si falla (tablet
 * offline en un túnel, backend viejo sin la query `communicationsByIds`,
 * error GraphQL, lo que sea) se ignora en silencio y el selector sigue
 * EXACTAMENTE como hoy — los per-row GET_COMMUNICATION_BY_ID intactos son el
 * fallback natural. Este helper NUNCA lanza.
 *
 * `fetchPolicy` (default `cache-first`, contrato de tablas/informes): los
 * DETALLES (sin polling ni flag reFetch que refresque después) pasan
 * `network-only` para que CADA carga baje las Communications frescas en un
 * solo round-trip — mismo razonamiento que `warmupOrdersByIds`.
 */
export async function warmupCommunicationsByIds(
  ids: (string | undefined | null)[],
  reFetch = false,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<void> {
  // Con reFetch=true el per-row va network-only e IGNORA la caché: el
  // warm-up solo añadiría una request extra con los mismos payloads que
  // las respuestas per-row ya traen. Solo aporta en el camino cache-first.
  if (reFetch) return;
  try {
    const uniqueIds = [
      ...new Set(ids.filter((id): id is string => Boolean(id))),
    ];
    if (uniqueIds.length === 0) return;

    const batches: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }

    await Promise.all(
      batches.map((batch) =>
        client.query({
          query: GET_COMMUNICATIONS_BY_IDS,
          variables: { ids: batch },
          fetchPolicy,
          // 'all' cubre "backend desplegado sin communicationsByIds" (campo
          // desconocido → error GraphQL → se ignora, no revienta).
          errorPolicy: 'all',
        })
      )
    );
  } catch (err) {
    // VACÍO de efectos a propósito: warm-up fallido = comportamiento de hoy.
    log('warm-up communicationsByIds omitido (sin efectos):', err);
  }
}
